import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Bell, LogOut, MessageCircle } from "lucide-react";
import BrandMark from "./BrandMark";
import AccountMenu from "./AccountMenu";
import useCreatorLiveCampaignAlerts from "../hooks/useCreatorLiveCampaignAlerts";
import useCampaignCompletionAlerts from "../hooks/useCampaignCompletionAlerts";
import useCampaignExtensionAlerts from "../hooks/useCampaignExtensionAlerts";
import useSupportChatNotifications from "../hooks/useSupportChatNotifications";
import useGeneralAnnouncements from "../hooks/useGeneralAnnouncements";
import { CompletionNoticeCard } from "./CampaignCompletionNotice";
import { ExtensionNoticeCard } from "./CampaignExtensionNotice";

const PUBLIC_CHAT_ROOM_ENABLED = process.env.REACT_APP_PUBLIC_CHAT_ROOM_ENABLED === "true";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notificationOpen, setNotificationOpen] = useState(false);
  const dashboardPath = user?.role === "admin" ? "/admin" : (user?.role === "brand" || user?.role === "client") ? "/client" : "/dashboard";
  const showProfileNotification = user?.role === "creator" && !user?.profile_complete;
  const {
    unseenNotifications: generalAnnouncements,
    unseenCount: generalAnnouncementCount,
    markSeen: markGeneralAnnouncementsSeen,
  } = useGeneralAnnouncements(user, { limit: 3 });
  const {
    unseenCampaigns,
    unseenCount: liveCampaignNotificationCount,
    markSeen: markLiveCampaignsSeen,
  } = useCreatorLiveCampaignAlerts(user, { limit: 4 });
  const {
    unseenNotifications: completionNotifications,
    unseenCount: completionNotificationCount,
    markSeen: markCompletionNotificationsSeen,
  } = useCampaignCompletionAlerts(user, { limit: 3 });
  const {
    unseenNotifications: extensionNotifications,
    unseenCount: extensionNotificationCount,
    markSeen: markExtensionNotificationsSeen,
  } = useCampaignExtensionAlerts(user, { limit: 3 });
  const {
    unreadCount: supportChatNotificationCount,
    unreadTickets: supportChatTickets,
  } = useSupportChatNotifications(user, { limit: 3 });
  const notificationCount = (showProfileNotification ? 1 : 0)
    + generalAnnouncementCount
    + liveCampaignNotificationCount
    + completionNotificationCount
    + extensionNotificationCount
    + supportChatNotificationCount;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[#FDFBF7] border-b-2 border-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
        <Link to={user && user !== false ? dashboardPath : "/"} data-testid="logo-link" className="flex min-w-0 shrink items-center">
          <BrandMark size="sm" />
        </Link>

        <nav className="hidden md:flex items-center gap-7 font-semibold text-sm">
          <Link to="/campaigns" data-testid="nav-campaigns" className="hover:text-[#FF2A7A] transition">Kampanye</Link>
          <Link to="/cara-kerja" data-testid="nav-how" className="hover:text-[#FF2A7A] transition">Cara Kerja</Link>
          <Link to="/leaderboard" data-testid="nav-leaderboard" className="hover:text-[#FF2A7A] transition">Leaderboard</Link>
        </nav>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {user && user !== false ? (
            <>
              <div className="relative">
                <button
                  type="button"
                  data-testid="header-bell"
                  onClick={() => setNotificationOpen((value) => !value)}
                  className={`inline-flex w-10 h-10 items-center justify-center border-2 border-[#0A0A0A] rounded-full shadow-[2px_2px_0_0_#0A0A0A] ${notificationCount ? "bg-[#FFD600]" : "bg-white"}`}
                  aria-label={notificationCount ? `${notificationCount} notifikasi` : "Notifikasi"}
                  aria-expanded={notificationOpen}
                >
                  <Bell size={16} />
                  {notificationCount > 0 && (
                    <span className="absolute -right-1 -top-1 min-w-5 h-5 px-1 rounded-full border-2 border-[#0A0A0A] bg-[#FF3D00] text-[#0A0A0A] text-[10px] font-black grid place-items-center leading-none">
                      {notificationCount}
                    </span>
                  )}
                </button>
                {notificationOpen && (
                  <div className="fixed left-3 right-3 top-[4.75rem] z-50 rounded-xl border-2 border-[#0A0A0A] bg-[#FDFBF7] p-3 shadow-[4px_4px_0_0_#0A0A0A] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-3 sm:w-80 sm:max-w-[calc(100vw-2rem)]">
                    <div className="flex items-center justify-between gap-3 border-b-2 border-[#0A0A0A]/10 pb-2">
                      <div className="font-display font-bold">Notifikasi</div>
                      {notificationCount > 0 && <span className="nb-tag bg-[#FF3D00] text-[#0A0A0A]">{notificationCount}</span>}
                    </div>
                    {notificationCount > 0 ? (
                      <div className="pt-3">
                        <div className="space-y-3">
                          {generalAnnouncements.length > 0 && (
                            <div className="rounded-lg border-2 border-[#0A0A0A] bg-[#B9A2FF] p-3">
                              <div className="flex items-center gap-2 text-sm font-bold">
                                <MessageCircle size={17} strokeWidth={3} />
                                Pengumuman Buzzer Klip
                              </div>
                              <div className="mt-2 space-y-2">
                                {generalAnnouncements.map((item) => (
                                  <div
                                    key={item.notification_key || item.id}
                                    className="rounded-lg border-2 border-[#0A0A0A] bg-white px-3 py-2"
                                  >
                                    <div className="text-xs font-bold">{item.title}</div>
                                    <p className="mt-1 text-xs leading-relaxed text-[#4A4A4A]">{item.message}</p>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        markGeneralAnnouncementsSeen([item]);
                                        setNotificationOpen(false);
                                        if (PUBLIC_CHAT_ROOM_ENABLED && item.can_open_room && user?.role === "creator") {
                                          window.sessionStorage.setItem("bk_open_chat_room", "1");
                                          window.dispatchEvent(new Event("buzzerklip:open-chat-room"));
                                        }
                                      }}
                                      className="nb-btn text-xs px-3 py-2 mt-2 w-full"
                                    >
                                      {PUBLIC_CHAT_ROOM_ENABLED && item.can_open_room && user?.role === "creator" ? "Buka Ruang Diskusi" : "Tandai Sudah Dibaca"}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {showProfileNotification && (
                            <div className="rounded-lg border-2 border-[#0A0A0A] bg-white p-3">
                              <div className="font-bold text-sm">Profile creator belum lengkap</div>
                              <ul className="mt-2 space-y-1 text-sm text-[#4A4A4A]">
                                <li>Lengkapi profile anda untuk data payout.</li>
                                <li>Lengkapi profile social media yang telah disediakan.</li>
                              </ul>
                              <Link
                                to="/profile"
                                onClick={() => setNotificationOpen(false)}
                                className="nb-btn nb-btn-secondary text-xs px-3 py-2 mt-3"
                              >
                                Lengkapi Profile
                              </Link>
                            </div>
                          )}
                          {unseenCampaigns.length > 0 && (
                            <div className="rounded-lg border-2 border-[#0A0A0A] bg-[#D4FF00] p-3">
                              <div className="font-bold text-sm">Campaign baru sudah live</div>
                              <div className="mt-2 space-y-2">
                                {unseenCampaigns.map((campaign) => (
                                  <Link
                                    key={campaign.notification_key || campaign.id}
                                    to={`/campaigns/${campaign.id}`}
                                    onClick={() => {
                                      markLiveCampaignsSeen([campaign]);
                                      setNotificationOpen(false);
                                    }}
                                    className="block rounded-lg border-2 border-[#0A0A0A] bg-white px-3 py-2 text-xs font-bold hover:bg-[#FDFBF7]"
                                  >
                                    <span className="line-clamp-1">{campaign.title}</span>
                                    <span className="block font-normal text-[#4A4A4A]">{campaign.brand_name}</span>
                                  </Link>
                                ))}
                              </div>
                              <div className="mt-3 grid grid-cols-2 gap-2">
                                <Link
                                  to="/campaigns"
                                  onClick={() => {
                                    markLiveCampaignsSeen(unseenCampaigns);
                                    setNotificationOpen(false);
                                  }}
                                  className="nb-btn nb-btn-secondary text-xs px-3 py-2"
                                >
                                  Lihat Semua
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => markLiveCampaignsSeen(unseenCampaigns)}
                                  className="nb-btn text-xs px-3 py-2"
                                >
                                  Sudah Dilihat
                                </button>
                              </div>
                            </div>
                          )}
                          {completionNotifications.length > 0 && (
                            <div className="rounded-lg border-2 border-[#0A0A0A] bg-[#00E5FF] p-3">
                              <div className="font-bold text-sm">Campaign selesai</div>
                              <div className="mt-2 space-y-2">
                                {completionNotifications.map((item) => (
                                  <CompletionNoticeCard
                                    key={item.notification_key || item.id}
                                    item={item}
                                    compact
                                    onSeen={() => {
                                      markCompletionNotificationsSeen([item]);
                                      setNotificationOpen(false);
                                    }}
                                  />
                                ))}
                              </div>
                              <button
                                type="button"
                                onClick={() => markCompletionNotificationsSeen(completionNotifications)}
                                className="nb-btn text-xs px-3 py-2 mt-3 w-full"
                              >
                                Tandai Semua Dilihat
                              </button>
                            </div>
                          )}
                          {extensionNotifications.length > 0 && (
                            <div className="rounded-lg border-2 border-[#0A0A0A] bg-[#FFD600] p-3">
                              <div className="font-bold text-sm">Campaign diperpanjang</div>
                              <div className="mt-2 space-y-2">
                                {extensionNotifications.map((item) => (
                                  <ExtensionNoticeCard
                                    key={item.notification_key || item.id}
                                    item={item}
                                    compact
                                    onSeen={() => {
                                      markExtensionNotificationsSeen([item]);
                                      setNotificationOpen(false);
                                    }}
                                  />
                                ))}
                              </div>
                              <button
                                type="button"
                                onClick={() => markExtensionNotificationsSeen(extensionNotifications)}
                                className="nb-btn text-xs px-3 py-2 mt-3 w-full"
                              >
                                Tandai Semua Dilihat
                              </button>
                            </div>
                          )}
                          {supportChatNotificationCount > 0 && (
                            <div className="rounded-lg border-2 border-[#0A0A0A] bg-[#FF7A00] p-3">
                              <div className="font-bold text-sm">Chat admin</div>
                              <div className="mt-2 space-y-2">
                                {supportChatTickets.map((ticket) => (
                                  <Link
                                    key={ticket.id}
                                    to="/support"
                                    onClick={() => setNotificationOpen(false)}
                                    className="block rounded-lg border-2 border-[#0A0A0A] bg-white px-3 py-2 text-xs font-bold hover:bg-[#FDFBF7]"
                                  >
                                    <span className="line-clamp-1">{ticket.subject || "Pesan Admin"}</span>
                                    <span className="block font-normal text-[#4A4A4A] line-clamp-1">{ticket.last_message_preview || "Ada pesan baru dari admin."}</span>
                                  </Link>
                                ))}
                              </div>
                              <Link
                                to="/support"
                                onClick={() => setNotificationOpen(false)}
                                className="nb-btn text-xs px-3 py-2 mt-3 w-full"
                              >
                                Buka Chat
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="pt-3 text-sm text-[#4A4A4A]">Tidak ada notifikasi baru.</div>
                    )}
                  </div>
                )}
              </div>
              <AccountMenu />
              <button onClick={async () => { await logout(); navigate("/"); }} data-testid="header-logout" className="nb-btn nb-btn-dark text-xs px-3 py-2"><LogOut size={14}/></button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login" className="nb-btn nb-btn-secondary text-[11px] px-3 py-2 sm:text-xs sm:px-4">Masuk</Link>
              <Link to="/register" data-testid="header-register" className="nb-btn text-[11px] px-3 py-2 sm:text-xs sm:px-4">
                <span className="sm:hidden">Daftar</span>
                <span className="hidden sm:inline">Daftar Gratis</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
